import React, { memo, useContext, useCallback } from 'react';
import type { AppContextValue } from 'src/components/app-context';
import { AppContext } from 'src/components/app-context';
import ProductRow from 'src/components/product-row';
import { ProductCard } from 'src/components/product-card';
import { ProductListItem } from 'src/components/product-list-item';
import { Tooltip } from '@prisjakt/ui/tooltip';
import { Direction } from '@prisjakt/ui';

const renderPropertyValue = (property: Property, t: AppContextValue['t']) => {
  if (property.type === 'boolean') {
    if (property.prettyTable === null) return '-';
    return property.prettyTable === 'true'
      ? t('category', 'property.boolean-true')
      : t('category', 'property.boolean-false');
  }

  return property.prettyTable;
};

type Property = {
  id: string;
  type: string;
  name: string;
  prettyTable: string;
  prettyVerbose: string;
};

type ProductProps = {
  id: number;
  mode: 'grid' | 'row' | 'table';
  link: string;
  name: string;
  image?: React.ReactNode;
  rating?: number;
  ratingCount?: number;
  category?: {
    name: string;
    pathName?: string;
  };
  // TODO: ProductRow takes Element, but ProductCard takes ReactElement, use any for now
  campaignBadge?: any;
  brand?: {
    name?: string;
    logo?: string;
    featured?: boolean;
  };
  storesCount?: number;
  storesCountText?: string;
  price?: string | number;
  properties?: Property[];
  selectable?: boolean;
  selected?: boolean;
  onSelect?: (id: number, type: string) => void;
  onDeselect?: (id: number, type: string) => void;
  onClick?: () => void;
  className?: string;
  onCategoryLinkClick?: (clickInfo: { url: string }) => void;
};

const Product: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<ProductProps>>
> = ({
  id,
  mode,
  link,
  name,
  image,
  price,
  rating,
  ratingCount,
  category,
  campaignBadge,
  brand,
  storesCount,
  storesCountText,
  properties = [],
  selectable,
  selected,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onSelect = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onDeselect = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onClick = () => {},
  className,
  onCategoryLinkClick,
}) => {
  const { t } = useContext(AppContext);
  const select = useCallback(() => onSelect(id, 'productCard'), [id, onSelect]);
  const deselect = useCallback(
    () => onDeselect(id, 'productCard'),
    [id, onDeselect]
  );

  switch (mode) {
    case 'row':
      return (
        <ProductRow
          link={link}
          name={name}
          image={image}
          price={price}
          rating={rating}
          ratingCount={ratingCount}
          subtitle={category && category.name}
          campaignBadge={campaignBadge}
          storesCount={storesCount}
          storesCountText={storesCountText}
          showDivider
          selectable={selectable}
          selected={selected}
          onSelect={select}
          onDeselect={deselect}
          onClick={onClick}
          className={className}
        />
      );
    case 'grid':
      return (
        <ProductCard
          id={id}
          link={link}
          name={name}
          image={image}
          price={price}
          rating={rating}
          ratingCount={ratingCount}
          category={category}
          campaignBadge={campaignBadge}
          brandLogo={
            brand && brand.featured && brand.logo ? (
              <img src={brand.logo} alt={brand.name} />
            ) : null
          }
          selectable={selectable}
          selected={selected}
          onSelect={select}
          onDeselect={deselect}
          onClick={onClick}
          className={className}
          onCategoryLinkClick={onCategoryLinkClick}
        />
      );
    case 'table':
      return (
        <ProductListItem
          as="li"
          link={link}
          name={name}
          image={image}
          price={price}
          rating={rating}
          count={ratingCount}
          campaignBadge={campaignBadge}
          storesCount={storesCount}
          storesCountTranslation={storesCountText}
          properties={properties.map(property => (
            <Tooltip
              key={property.id}
              placement={Direction.Left}
              title={
                // eslint-disable-next-line no-nested-ternary
                property.prettyTable !== null
                  ? property.type === 'boolean'
                    ? `${property.name}: ${renderPropertyValue(property, t)}`
                    : property.prettyVerbose
                  : undefined
              }
            >
              {renderPropertyValue(property, t)}
            </Tooltip>
          ))}
          selectable={selectable}
          selected={selected}
          onSelect={select}
          onDeselect={deselect}
          onClick={onClick}
          className={className}
        />
      );
    default:
      return null;
  }
};

export default memo(Product);
